import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, LogOut } from 'lucide-react'
import { useAuth } from '@claritio/frontend/lib/auth'
import { RESPONDENT_MODULES, respondentModuleLabel, respondentModuleSoon, respondentFooterVariant } from '@claritio/frontend/lib/respondentNav'
import { useInterviewContext } from '@claritio/frontend/lib/interviewContext'
import { LogoMark } from '@claritio/frontend/components/Logo'
import OrgBadge from '@claritio/frontend/components/ui/OrgBadge'
import Avatar from '@claritio/frontend/components/ui/Avatar'
import LanguageToggle from '@claritio/frontend/components/LanguageToggle'
import { Popover, PopoverTrigger, PopoverContent } from '@claritio/frontend/components/ui/popover'
import SidebarFrame, { NavRow } from '@claritio/frontend/components/sidebar/SidebarFrame'
import { cn } from '@claritio/frontend/lib/utils'
import { showComingSoon } from '@claritio/frontend/lib/comingSoon'

// ENG-133 — the respondent sidebar: a thin config over the shared SidebarFrame,
// client-first (the employer's badge on top, Claritio reduced to a "Powered by" mark).
// Footer adapts to who's in: an account respondent gets avatar + settings + log out;
// a magic-link visitor (no account) gets language + the Powered-by mark only.
export default function RespondentSidebar({
  collapsed,
  canCollapse = true,
  onToggleCollapse,
  orgName,
  orgLogoUrl,
  interviewStatus,
}: {
  collapsed: boolean
  canCollapse?: boolean
  onToggleCollapse: () => void
  orgName: string | null
  orgLogoUrl: string | null
  interviewStatus?: string
}) {
  const { t } = useTranslation('interview')
  const { me, logout } = useAuth()
  const { welcomeUrl, chatUrl, doneUrl } = useInterviewContext()
  const navigate = useNavigate()
  const currentPath = useLocation().pathname
  const isAccount = respondentFooterVariant(!!me?.user) === 'account'

  async function handleSignOut() {
    if (await logout()) navigate('/employee/login', { replace: true })
  }

  const header = (
    <div className="flex h-[var(--app-header)] shrink-0 items-center gap-2.5 border-b px-4">
      <OrgBadge name={orgName ?? '?'} size={28} logoUrl={orgLogoUrl} />
      {!collapsed && (
        <div className="min-w-0 leading-tight">
          <div className="truncate text-compact font-medium">{orgName ?? '…'}</div>
          <div className="text-caption text-muted-foreground">{t('shell.assessmentLabel')}</div>
        </div>
      )}
    </div>
  )

  const scroll = (
    <div className="p-2.5">
      {RESPONDENT_MODULES.map((m) => {
        const ctx = { welcomeUrl, chatUrl, doneUrl, status: interviewStatus, currentPath, reportAccess: !!me?.user?.report_access }
        const soon = respondentModuleSoon(m, ctx)
        if (soon && !showComingSoon()) return null
        return (
          <NavRow
            key={m.key}
            item={{
              to: soon ? '' : m.to?.(ctx) ?? '',
              icon: m.icon,
              label: respondentModuleLabel(m),
              soon,
              soonLabel: soon ? t('shell.nav.soon') : undefined,
            }}
            collapsed={collapsed}
          />
        )
      })}
    </div>
  )

  // Small, unobtrusive Claritio mark — infrastructure role, not competing with the
  // employer's identity (ENG-133 branding decision).
  const poweredBy = (
    <div className={cn('flex shrink-0 items-center gap-1.5 border-t px-4 py-2 text-caption text-muted-foreground', collapsed && 'justify-center px-0')}>
      <LogoMark className="size-3.5" />
      {!collapsed && <span>{t('shell.poweredBy')}</span>}
    </div>
  )

  const account = (
    // Height from the shared --app-header token (not padding) so the row is the
    // same whether the rail is expanded or collapsed — the divider never shifts
    // on fold — and the footer mirrors the header height (ENG-133).
    <div className="flex h-[var(--app-header)] shrink-0 items-center justify-between gap-2 border-t px-4">
      {isAccount ? (
        <>
          {!collapsed && (
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar name={me?.user?.name || me?.user?.email} size={28} />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-xs font-medium">{me?.user?.name || me?.user?.email}</div>
                <div className="text-caption text-muted-foreground">{t('shell.respondentLabel')}</div>
              </div>
            </div>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button
                title={t('shell.settings')}
                className={cn('rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground', collapsed && 'mx-auto')}
              >
                {collapsed ? <Avatar name={me?.user?.name || me?.user?.email} size={24} /> : <Settings className="size-4" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-52 space-y-1.5">
              <div className="flex items-center justify-between gap-2 px-1">
                <span className="text-compact text-muted-foreground">{t('shell.language')}</span>
                <LanguageToggle />
              </div>
              <button onClick={handleSignOut} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-compact hover:bg-accent">
                <LogOut className="size-3.5" /> {t('shell.signOut')}
              </button>
            </PopoverContent>
          </Popover>
        </>
      ) : collapsed ? (
        <Popover>
          <PopoverTrigger asChild>
            <button title={t('shell.language')} className="mx-auto rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <Settings className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" className="w-auto">
            <LanguageToggle />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-compact text-muted-foreground">{t('shell.language')}</span>
          <LanguageToggle />
        </div>
      )}
    </div>
  )

  const footer = (
    <>
      {account}
      {poweredBy}
    </>
  )

  return (
    <SidebarFrame
      header={header}
      scroll={scroll}
      footer={footer}
      collapsed={collapsed}
      canCollapse={canCollapse}
      onToggleCollapse={onToggleCollapse}
    />
  )
}
